import { useCallback, useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Ticket, Plus, Clock, CheckCircle2, MessageSquare, X, User, LifeBuoy } from "lucide-react";
import { toast } from "sonner";

const PRIORITY_TONE = {
    low: "bg-zinc-100 text-zinc-600 border-zinc-200",
    normal: "bg-blue-50 text-blue-700 border-blue-200",
    high: "bg-orange-50 text-orange-700 border-orange-200",
    critical: "bg-red-50 text-red-700 border-red-300 font-semibold",
};

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });
    } catch { return iso; }
}
function formatDateTime(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" }) + " IST";
    } catch { return iso; }
}

/**
 * Support — customer's ticket history across all their plants + raise-a-ticket flow.
 */
export default function Support() {
    const [tickets, setTickets] = useState([]);
    const [plants, setPlants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("open");
    const [showCreate, setShowCreate] = useState(false);
    const [selected, setSelected] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [t, s] = await Promise.all([
                api.get("/customer/tickets"),
                api.get("/customer/plants"),
            ]);
            setTickets(t.data);
            setPlants(s.data || []);
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const filtered = tickets.filter(t => tab === "all" || (tab === "open" ? t.status === "open" : t.status === "closed"));
    const openCount = tickets.filter(t => t.status === "open").length;
    const closedCount = tickets.filter(t => t.status === "closed").length;

    return (
        <div className="space-y-6" data-testid="cust-support-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Support</div>
                    <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-900 mt-1">Your tickets</h1>
                    <div className="text-sm text-zinc-500 mt-1">Raise a request and our engineers pick it up within one working day.</div>
                </div>
                <Button
                    onClick={() => setShowCreate(true)}
                    disabled={plants.length === 0}
                    className="bg-[#0A0A0A] hover:bg-black text-white rounded-full h-10"
                    data-testid="cust-raise-ticket-btn"
                >
                    <Plus className="w-4 h-4 mr-1.5" /> Raise a Ticket
                </Button>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <SummaryCard icon={Ticket} label="Total tickets" value={tickets.length} testid="cust-summary-total" />
                <SummaryCard icon={Clock} label="Open" value={openCount} accent="orange" testid="cust-summary-open" />
                <SummaryCard icon={CheckCircle2} label="Closed" value={closedCount} accent="green" testid="cust-summary-closed" />
            </div>

            {/* Tabs */}
            <div className="inline-flex rounded-sm border border-zinc-200 overflow-hidden">
                {[["open", "Open"], ["closed", "Closed"], ["all", "All"]].map(([k, l]) => (
                    <button
                        key={k}
                        onClick={() => setTab(k)}
                        className={`px-4 py-1.5 text-xs uppercase tracking-wider font-medium transition-colors ${tab === k ? "bg-[#0B0D11] text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
                        data-testid={`cust-tickets-tab-${k}`}
                    >
                        {l}
                    </button>
                ))}
            </div>

            {/* Ticket table */}
            <div className="bg-white border border-zinc-200 rounded-sm">
                {loading ? (
                    <div className="p-6 text-sm text-zinc-500">Loading tickets…</div>
                ) : filtered.length === 0 ? (
                    <div className="p-8 text-center" data-testid="cust-no-tickets">
                        <LifeBuoy className="w-8 h-8 mx-auto text-zinc-300 mb-3" />
                        <div className="text-sm text-zinc-600 font-medium">
                            {tab === "open" ? "No open tickets — you're all set." : tab === "closed" ? "No closed tickets yet." : "No tickets yet."}
                        </div>
                        {tab === "open" && plants.length > 0 && (
                            <div className="text-xs text-zinc-500 mt-1">Something not right? Click <span className="text-[#0A0A0A] font-medium">Raise a Ticket</span> above.</div>
                        )}
                    </div>
                ) : (
                    <table className="w-full text-sm" data-testid="cust-tickets-table">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-50 border-b border-zinc-200">
                                <th className="py-2 px-4 text-left font-semibold">Ticket #</th>
                                <th className="py-2 px-4 text-left font-semibold">Plant</th>
                                <th className="py-2 px-4 text-left font-semibold">Subject</th>
                                <th className="py-2 px-4 text-left font-semibold">Opened</th>
                                <th className="py-2 px-4 text-left font-semibold">Days Open</th>
                                <th className="py-2 px-4 text-left font-semibold">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((t, i) => (
                                <tr key={t.id} className="border-b border-zinc-100 hover:bg-zinc-50/60" data-testid={`cust-ticket-row-${i}`}>
                                    <td className="py-2.5 px-4">
                                        <button onClick={() => setSelected(t)} className="font-mono text-xs text-[#0A0A0A] hover:underline font-medium" data-testid={`cust-ticket-num-${i}`}>
                                            {t.ticket_number}
                                        </button>
                                        {(t.remarks || []).length > 0 && (
                                            <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] font-mono text-zinc-500">
                                                <MessageSquare className="w-2.5 h-2.5" /> {t.remarks.length}
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-2.5 px-4">
                                        <div className="text-sm text-zinc-900 font-medium">{t.customer_name || t.plant_name}</div>
                                        {t.location && <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{t.location}</div>}
                                    </td>
                                    <td className="py-2.5 px-4">
                                        <button onClick={() => setSelected(t)} className="text-left">
                                            <div className="text-sm text-zinc-800 hover:text-[#0A0A0A]">{t.subject}</div>
                                        </button>
                                    </td>
                                    <td className="py-2.5 px-4 font-mono text-xs text-zinc-700">{formatDate(t.opening_date)}</td>
                                    <td className="py-2.5 px-4 font-mono text-xs">
                                        <span className={t.status === "open" ? "text-[#DC2626]" : "text-zinc-500"}>{t.days_open}d</span>
                                    </td>
                                    <td className="py-2.5 px-4">
                                        {t.status === "open" ? (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-red-50 text-[#DC2626] border border-red-200">
                                                <Clock className="w-2.5 h-2.5" /> OPEN
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-green-50 text-[#16A34A] border border-green-200">
                                                <CheckCircle2 className="w-2.5 h-2.5" /> CLOSED
                                            </span>
                                        )}
                                        <span className={`inline-block ml-1.5 text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${PRIORITY_TONE[t.priority] || PRIORITY_TONE.normal}`}>
                                            {(t.priority || "normal").toUpperCase()}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {showCreate && plants.length > 0 && (
                <RaiseTicketDialog
                    plants={plants}
                    onClose={() => setShowCreate(false)}
                    onCreated={() => { setShowCreate(false); load(); }}
                />
            )}
            {selected && (
                <TicketDetailDialog
                    ticketId={selected.id}
                    onClose={() => setSelected(null)}
                    onChanged={load}
                />
            )}
        </div>
    );
}

function SummaryCard({ icon: Icon, label, value, accent = "black", testid }) {
    const c = { orange: "text-[#0A0A0A]", green: "text-[#16A34A]", black: "text-zinc-900" }[accent];
    return (
        <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid={testid}>
            <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">{label}</div>
                <Icon className="w-4 h-4 text-zinc-300" />
            </div>
            <div className={`text-3xl font-mono font-medium ${c}`}>{value}</div>
        </div>
    );
}

function RaiseTicketDialog({ plants, onClose, onCreated }) {
    const [stationId, setStationId] = useState(plants[0].id);
    const [subject, setSubject] = useState("");
    const [description, setDescription] = useState("");
    const [priority, setPriority] = useState("normal");
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (subject.trim().length < 3) {
            toast.error("Subject must be at least 3 characters");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post(`/plants/${stationId}/tickets`, {
                subject: subject.trim(),
                description: description.trim() || null,
                priority,
            });
            toast.success(`Ticket ${data.ticket_number} raised — we'll get on it.`);
            onCreated();
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="cust-raise-modal">
            <div className="bg-white rounded-sm w-full max-w-lg" onClick={e => e.stopPropagation()}>
                <div className="p-5 border-b border-zinc-200 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Raise a Ticket</div>
                        <div className="text-sm text-zinc-700 mt-0.5">Tell us what's happening — our engineers pick it up.</div>
                    </div>
                    <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-4">
                    {plants.length > 1 && (
                        <div>
                            <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 block">Plant</label>
                            <select
                                value={stationId}
                                onChange={e => setStationId(Number(e.target.value))}
                                className="w-full h-9 rounded-sm border border-zinc-200 px-3 text-sm bg-white"
                                data-testid="cust-ticket-plant-select"
                            >
                                {plants.map(p => (
                                    <option key={p.id} value={p.id}>{p.customer_name || p.name} · {p.installed_capacity_kw} kWp</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div>
                        <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 block">Subject</label>
                        <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="e.g. Inverter beeping since morning" className="rounded-sm" data-testid="cust-ticket-subject-input" />
                    </div>
                    <div>
                        <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 block">Description (optional)</label>
                        <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} placeholder="Details, timestamps, photos or context that would help our engineers…" className="rounded-sm" data-testid="cust-ticket-desc-input" />
                    </div>
                    <div>
                        <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 block">Priority</label>
                        <div className="inline-flex rounded-sm border border-zinc-200 overflow-hidden">
                            {["low", "normal", "high", "critical"].map(p => (
                                <button
                                    key={p}
                                    onClick={() => setPriority(p)}
                                    className={`px-3 py-1.5 text-xs uppercase tracking-wider font-medium ${priority === p ? "bg-[#0B0D11] text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
                                    data-testid={`cust-priority-${p}`}
                                >{p}</button>
                            ))}
                        </div>
                        <div className="text-[10px] text-zinc-400 mt-1.5 leading-relaxed">
                            <span className="font-semibold text-red-600">Critical</span> — plant is offline or unsafe.
                            {" "}<span className="font-semibold text-orange-600">High</span> — production severely reduced.
                            {" "}<span className="font-semibold text-blue-600">Normal</span> — standard service request.
                            {" "}<span className="font-semibold text-zinc-600">Low</span> — nice-to-have or cosmetic.
                        </div>
                    </div>
                </div>
                <div className="p-5 border-t border-zinc-200 flex items-center justify-end gap-2">
                    <Button variant="ghost" onClick={onClose} className="rounded-sm">Cancel</Button>
                    <Button onClick={submit} disabled={saving} className="bg-[#16A34A] hover:bg-[#128F3F] text-white rounded-sm" data-testid="cust-submit-ticket-btn">
                        {saving ? "Raising…" : "Raise Ticket"}
                    </Button>
                </div>
            </div>
        </div>
    );
}

function TicketDetailDialog({ ticketId, onClose, onChanged }) {
    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [remark, setRemark] = useState("");
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/tickets/${ticketId}`);
            setTicket(data);
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally { setLoading(false); }
    }, [ticketId]);
    useEffect(() => { load(); }, [load]);

    const submitRemark = async () => {
        if (remark.trim().length < 1) return;
        setSaving(true);
        try {
            const { data } = await api.post(`/tickets/${ticketId}/remarks`, { remark: remark.trim() });
            setTicket(data);
            setRemark("");
            toast.success("Remark added");
            onChanged();
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="cust-ticket-detail-modal">
            <div className="bg-white rounded-sm w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="p-5 border-b border-zinc-200 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Ticket Detail</div>
                        {ticket && (
                            <div className="flex items-baseline gap-2 mt-0.5 flex-wrap">
                                <div className="font-mono text-sm text-zinc-900 font-medium">{ticket.ticket_number}</div>
                                <div className="text-sm text-zinc-700">· {ticket.subject}</div>
                            </div>
                        )}
                    </div>
                    <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700 flex-shrink-0"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 overflow-y-auto flex-1">
                    {loading || !ticket ? (
                        <div className="text-sm text-zinc-500">Loading ticket…</div>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                                <Meta label="Opened" value={formatDate(ticket.opening_date)} />
                                <Meta label="Closed" value={ticket.closing_date ? formatDate(ticket.closing_date) : "—"} />
                                <Meta label="Days Open" value={`${ticket.days_open}d`} />
                                <Meta label="Status" value={ticket.status === "open" ? "OPEN" : "CLOSED"} tone={ticket.status === "open" ? "red" : "green"} />
                            </div>
                            {ticket.description && (
                                <div className="mb-5 bg-zinc-50 border border-zinc-200 rounded-sm p-3">
                                    <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1">Initial description</div>
                                    <div className="text-sm text-zinc-700 whitespace-pre-wrap">{ticket.description}</div>
                                </div>
                            )}
                            <div className="mb-5">
                                <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
                                    <MessageSquare className="w-3 h-3" /> Timeline ({(ticket.remarks || []).length})
                                </div>
                                {(ticket.remarks || []).length === 0 ? (
                                    <div className="text-xs text-zinc-500 italic py-2">No engineer notes yet — we'll update this thread as work progresses.</div>
                                ) : (
                                    <div className="space-y-2">
                                        {ticket.remarks.map((r, i) => (
                                            <div key={r.id || i} className={`rounded-sm p-3 border ${r.is_resolution ? "bg-green-50 border-green-200" : "bg-white border-zinc-200"}`} data-testid={`cust-remark-${i}`}>
                                                <div className="flex items-center gap-2 mb-1 text-[11px] font-mono text-zinc-500">
                                                    <User className="w-3 h-3" />
                                                    <span className="text-zinc-800 font-medium">{r.author_name || r.author}</span>
                                                    {r.author_role && <span className="uppercase text-[10px] text-zinc-400">· {r.author_role.replace("_", " ")}</span>}
                                                    <span className="text-zinc-300 ml-auto">{formatDateTime(r.created_at)}</span>
                                                </div>
                                                <div className="text-sm text-zinc-800 whitespace-pre-wrap">{r.text}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {ticket.status === "open" && (
                                <div className="border border-zinc-200 rounded-sm p-3 bg-white">
                                    <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 block">Add note</label>
                                    <Textarea
                                        value={remark}
                                        onChange={e => setRemark(e.target.value)}
                                        rows={3}
                                        placeholder="Anything new to add? Photos, timings, findings…"
                                        className="rounded-sm"
                                        data-testid="cust-add-remark-input"
                                    />
                                    <div className="flex items-center justify-end mt-2">
                                        <Button
                                            size="sm"
                                            onClick={submitRemark}
                                            disabled={saving || remark.trim().length < 1}
                                            className="bg-[#0A0A0A] hover:bg-black text-white rounded-full h-8"
                                            data-testid="cust-submit-remark-btn"
                                        >
                                            {saving ? "Saving…" : "Add Note"}
                                        </Button>
                                    </div>
                                </div>
                            )}
                            {ticket.status === "closed" && ticket.resolution_remark && (
                                <div className="mt-4 bg-green-50 border border-green-200 rounded-sm p-3">
                                    <div className="text-[10px] font-semibold uppercase tracking-widest text-green-700 mb-1 flex items-center gap-1.5">
                                        <CheckCircle2 className="w-3 h-3" /> Resolved
                                    </div>
                                    <div className="text-sm text-zinc-800">{ticket.resolution_remark}</div>
                                    <div className="text-[11px] font-mono text-zinc-500 mt-1">
                                        Closed by {ticket.closed_by} · {formatDate(ticket.closing_date)}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function Meta({ label, value, tone }) {
    const color = tone === "red" ? "text-[#DC2626]" : tone === "green" ? "text-[#16A34A]" : "text-zinc-900";
    return (
        <div>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">{label}</div>
            <div className={`text-sm font-mono mt-0.5 ${color}`}>{value}</div>
        </div>
    );
}
